import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getAuth, isSuperAdmin } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Settings as SettingsIcon, Building2, Users, Trash2, UserPlus, Mail, Copy, Inbox, Clock, Power, Zap, CheckCircle2, Check, AlertTriangle, Info, HelpCircle, ExternalLink, Book, Eye, ShieldCheck, Monitor, Smartphone } from 'lucide-react';

export const Settings = () => {
  const navigate = useNavigate();
  const { user, accounts } = getAuth();
  const currentAccountStr = localStorage.getItem('current_account');
  
  // Auto-select first account if none selected
  let currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  if (!currentAccount && accounts && accounts.length > 0) {
    currentAccount = accounts[0];
    localStorage.setItem('current_account', JSON.stringify(currentAccount));
  }
  
  const [activeTab, setActiveTab] = useState('organisation');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Organisation settings
  const [orgSettings, setOrgSettings] = useState({
    organisation_name: '',
    account_no: '',
  });
  
  // Integration settings
  const [integrationSettings, setIntegrationSettings] = useState({
    enable_billportal_format: false,
  });
  
  // API Key management
  const [apiKeyStatus, setApiKeyStatus] = useState({ hasKey: false, isActive: false });
  const [newApiKey, setNewApiKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [showApiDocsModal, setShowApiDocsModal] = useState(false);
  
  // User management
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'viewer' });
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  
  // Inbox settings
  const [inboxSettings, setInboxSettings] = useState(null);
  
  // Parser Config settings (simplified - validation deprecated)
  const [parserConfig, setParserConfig] = useState({
    parsing_mode: 'hybrid',
    ocr_engine: 3,
  });
  const [loadingParserConfig, setLoadingParserConfig] = useState(false);

  const isAdmin = currentAccount?.role === 'admin' || user?.is_super_admin;

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchOrgDetails();
    fetchInboxSettings();
    fetchIntegrationSettings();
    fetchApiKeyStatus();
    fetchParserConfig();
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.id, navigate]);

  const fetchOrgDetails = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/details`);
      setOrgSettings({
        organisation_name: data.organisation_name || data.company_name || '',
        account_no: data.account_no || '',
      });
    } catch (error) {
      console.error('Failed to load org details');
    }
  };

  const fetchUsers = async () => {
    if (!currentAccount || !isAdmin) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users`);
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchInboxSettings = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/inbox`);
      setInboxSettings(data);
    } catch (error) {
      console.error('Failed to load inbox settings');
    }
  };

  const fetchIntegrationSettings = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/settings`);
      setIntegrationSettings({
        enable_billportal_format: data.enable_billportal_format || false,
      });
    } catch (error) {
      console.error('Failed to load integration settings');
    }
  };

  const fetchParserConfig = async () => {
    if (!currentAccount || !isAdmin) return;
    setLoadingParserConfig(true);
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/parsing-config`);
      setParserConfig({
        parsing_mode: data.parsing_mode || 'hybrid',
        ocr_engine: data.ocr_engine || 3,
      });
    } catch (error) {
      console.error('Failed to load parser config');
    } finally {
      setLoadingParserConfig(false);
    }
  };

  const handleSaveParserConfig = async () => {
    if (!currentAccount || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${currentAccount.id}/parsing-config`, {
        ...parserConfig,
        parsing_mode: 'hybrid',
        ocr_enabled: true,
        ocr_provider: 'ocr_space',
        llm_provider: 'openai',
        llm_model: 'gpt-5.2',
        llm_api_key_source: 'global',
      });
      toast.success('Parser configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save parser config');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIntegrationSettings = async () => {
    if (!currentAccount || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${currentAccount.id}/settings`, {
        ai_provider: 'emergent',
        model_name: 'gpt-5.2',
        enable_billportal_format: integrationSettings.enable_billportal_format,
      });
      toast.success('Integration settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fetchApiKeyStatus = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/details`);
      setApiKeyStatus({
        hasKey: !!data.api_key,
        isActive: data.api_key_active || false,
        createdAt: data.api_key_created_at
      });
    } catch (error) {
      console.error('Failed to load API key status');
    }
  };

  const handleGenerateApiKey = async () => {
    if (!currentAccount || !isAdmin) return;
    if (!window.confirm('This will generate a new API key. Any existing key will be replaced. Continue?')) return;
    
    setGeneratingKey(true);
    try {
      const { data } = await api.post(`/accounts/${currentAccount.id}/generate-api-key`);
      setNewApiKey(data.api_key);
      setApiKeyStatus({ hasKey: true, isActive: true, createdAt: new Date().toISOString() });
      toast.success('API key generated! Copy it now - it won\'t be shown again.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!currentAccount || !isAdmin) return;
    if (!window.confirm('This will revoke the API key. External integrations will stop working. Continue?')) return;
    
    try {
      await api.delete(`/accounts/${currentAccount.id}/revoke-api-key`);
      setApiKeyStatus({ hasKey: true, isActive: false });
      setNewApiKey(null);
      toast.success('API key revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleSaveOrg = async () => {
    if (!currentAccount || !isAdmin) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${currentAccount.id}/details`, orgSettings);
      // Update local storage with new name
      const updatedAccount = { ...currentAccount, organisation_name: orgSettings.organisation_name };
      localStorage.setItem('current_account', JSON.stringify(updatedAccount));
      toast.success('Organisation details saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounts/${currentAccount.id}/users/invite`, inviteForm);
      toast.success(`Invited ${inviteForm.email}`);
      setInviteForm({ email: '', name: '', role: 'viewer' });
      setShowInvite(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/accounts/${currentAccount.id}/users/${userId}/role`, { role: newRole });
      toast.success('Role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;
    try {
      await api.delete(`/accounts/${currentAccount.id}/users/${userId}`);
      toast.success('User removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleUpdateUserStatus = async (userId, newStatus) => {
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await api.patch(`/accounts/${currentAccount.id}/users/${userId}/status`, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleTogglePfReviewer = async (userId, enabled) => {
    // Optimistic update
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, pf_reviewer: enabled } : u));
    try {
      await api.patch(`/accounts/${currentAccount.id}/users/${userId}/pf-reviewer`, { pf_reviewer: enabled });
      toast.success(enabled ? 'PF Reviewer enabled' : 'PF Reviewer disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PF reviewer flag');
      fetchUsers();
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  // Parser Config only matters when the intake-hub feature is enabled on
  // the org. Otherwise the tab adds clutter for orgs that don't use it.
  const isIntakeHubEnabled = currentAccount?.enabled_apps?.intake_hub === true;

  const tabs = [
    { id: 'organisation', label: 'Organisation', icon: Building2, adminOnly: false },
    ...(isIntakeHubEnabled ? [{ id: 'parser', label: 'Parser Config', icon: Zap, adminOnly: true }] : []),
    { id: 'integrations', label: 'Integrations', icon: Power, adminOnly: true },
    { id: 'users', label: 'Users', icon: Users, adminOnly: true },
    { id: 'feature-visibility', label: 'Feature Visibility', icon: Eye, adminOnly: true },
    { id: 'inbox', label: 'Email Inbox', icon: Inbox, adminOnly: false },
    { id: 'trusted-devices', label: 'Trusted Devices', icon: ShieldCheck, adminOnly: false },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <SettingsIcon className="h-6 w-6" strokeWidth={2} />
              <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Settings
              </h1>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {orgSettings.organisation_name || currentAccount?.organisation_name}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-[1800px] px-6 md:px-12 py-8">
        <div className="flex gap-8">
          {/* Vertical Tab Menu */}
          <div className="w-56 shrink-0">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                if (tab.adminOnly && !isAdmin) return null;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-none transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className={`flex-1 ${activeTab === 'users' || activeTab === 'feature-visibility' || activeTab === 'inbox' ? 'max-w-none' : 'max-w-3xl'}`}>
            {/* Organisation Tab */}
            {activeTab === 'organisation' && (
              <div className="border border-border rounded-none p-8 bg-card">
                <h2 className="text-2xl font-bold tracking-tight mb-6">Organisation Details</h2>
                <div className="space-y-6">
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Organisation Name
                    </Label>
                    <Input
                      className="rounded-none"
                      value={orgSettings.organisation_name}
                      onChange={(e) => setOrgSettings({ ...orgSettings, organisation_name: e.target.value })}
                      disabled={!isAdmin}
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Account Number
                    </Label>
                    <Input
                      className="rounded-none font-mono"
                      value={orgSettings.account_no}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground mt-1">Account numbers are system-generated</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Organisation ID
                    </Label>
                    <Input
                      className="rounded-none font-mono bg-muted"
                      value={currentAccount?.id || ''}
                      disabled
                    />
                    <p className="text-xs text-muted-foreground mt-1">System-generated unique identifier</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                      Your Role
                    </Label>
                    <Input
                      className="rounded-none capitalize bg-muted"
                      value={currentAccount?.role || ''}
                      disabled
                    />
                  </div>
                  {isAdmin && (
                    <Button
                      className="rounded-none font-bold uppercase"
                      onClick={handleSaveOrg}
                      disabled={saving}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  )}
                  {!isAdmin && (
                    <p className="text-sm text-muted-foreground">Only admins can edit organisation details.</p>
                  )}
                </div>
              </div>
            )}

            {/* Parser Config Tab */}
            {activeTab === 'parser' && isAdmin && (
              <div className="border border-border rounded-none p-8 bg-card">
                <h2 className="text-2xl font-bold tracking-tight mb-2">Parser Configuration</h2>
                <p className="text-muted-foreground mb-6">
                  Configure how bills are parsed and validated for this organization.
                </p>

                {loadingParserConfig ? (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 mx-auto text-muted-foreground animate-spin" />
                    <p className="text-muted-foreground mt-2">Loading parser configuration...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Parsing Info */}
                    <div className="border border-border rounded-none p-6 bg-muted/20">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="h-5 w-5 text-primary" />
                        <h3 className="font-bold text-lg">Parser Settings</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Parsing mode and OCR settings are managed at the Super Admin level.
                        Bills are parsed via the AI pipeline and stored for review.
                      </p>
                      <div className="flex gap-4">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Mode:</span>{' '}
                          <Badge variant="outline" className="rounded-none">{parserConfig.parsing_mode || 'hybrid'}</Badge>
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">OCR Engine:</span>{' '}
                          <Badge variant="outline" className="rounded-none">v{parserConfig.ocr_engine || 3}</Badge>
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <Button
                      className="rounded-none font-bold uppercase w-full"
                      onClick={handleSaveParserConfig}
                      disabled={saving}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Parser Configuration'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && isAdmin && (
              <div className="border border-border rounded-none p-8 bg-card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold tracking-tight">Integrations</h2>
                  <Dialog open={showApiDocsModal} onOpenChange={setShowApiDocsModal}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-none gap-2" data-testid="api-docs-help-btn">
                        <Book className="h-4 w-4" />
                        API Documentation
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-none">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2">
                          <Book className="h-5 w-5" />
                          BillPortal External API Documentation
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-6 mt-4">
                        {/* Authentication */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                            <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">1</span>
                            Authentication
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            All API requests require an API key passed via the <code className="bg-muted px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header.
                          </p>
                          <div className="bg-muted/50 p-3 rounded-none">
                            <code className="text-xs font-mono">X-API-Key: your_api_key_here</code>
                          </div>
                        </div>

                        {/* Upload APIs */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                            Upload & Process Bills
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-none border-l-4 border-blue-500">
                              <p className="font-mono text-sm font-bold text-blue-700 dark:text-blue-400">POST /api/external/v1/documents/upload</p>
                              <p className="text-xs text-muted-foreground mt-1">Upload a PDF bill for processing. Returns document ID for tracking.</p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><span className="font-semibold">Form Data:</span></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><code className="bg-muted px-1">file</code> - PDF file (required)</li>
                                  <li><code className="bg-muted px-1">external_id</code> - Your reference ID (optional, must be unique)</li>
                                  <li><code className="bg-muted px-1">reprocess</code> - Set to "true" to replace existing doc with same external_id</li>
                                </ul>
                              </div>
                              <div className="mt-3 bg-slate-900 text-slate-100 p-2 rounded text-[10px] font-mono overflow-x-auto">
                                <pre>{`curl -X POST "${window.location.origin}/api/external/v1/documents/upload" \\
  -H "X-API-Key: your_api_key" \\
  -F "file=@/path/to/bill.pdf" \\
  -F "external_id=YOUR-TRACKING-ID-123"`}</pre>
                              </div>
                            </div>

                            <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-none border-l-4 border-green-500">
                              <p className="font-mono text-sm font-bold text-green-700 dark:text-green-400">GET /api/external/v1/documents/by-external-id/{'{external_id}'}</p>
                              <p className="text-xs text-muted-foreground mt-1">Get document status and parsed data by your external reference ID.</p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><span className="font-semibold">Query Params:</span></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><code className="bg-muted px-1">include_parsed_data</code> - Include extracted data (default: true)</li>
                                  <li><code className="bg-muted px-1">include_pdf_url</code> - Include presigned PDF URL (default: false)</li>
                                </ul>
                              </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-950/30 p-3 rounded-none border-l-4 border-slate-500">
                              <p className="font-mono text-sm font-bold">GET /api/external/v1/documents/{'{document_id}'}/status</p>
                              <p className="text-xs text-muted-foreground mt-1">Poll for document processing status.</p>
                              <p className="text-xs mt-2"><span className="font-semibold">Status values:</span> <code className="bg-muted px-1">uploaded</code>, <code className="bg-muted px-1">processing</code>, <code className="bg-muted px-1">completed</code>, <code className="bg-muted px-1">failed</code></p>
                            </div>
                          </div>
                        </div>

                        {/* BillPortal Format */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                            BillPortal Format Export
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-purple-50 dark:bg-purple-950/30 p-3 rounded-none border-l-4 border-purple-500">
                              <p className="font-mono text-sm font-bold text-purple-700 dark:text-purple-400">POST /api/external/v1/documents/by-external-id/{'{external_id}'}/convert-billportal</p>
                              <p className="text-xs text-muted-foreground mt-1">Convert a completed document to BillPortal JSON format.</p>
                              <p className="text-xs mt-2 text-amber-600 dark:text-amber-400">⚠️ Document must be in 'completed' status.</p>
                            </div>

                            <div className="bg-purple-50 dark:bg-purple-950/30 p-3 rounded-none border-l-4 border-purple-500">
                              <p className="font-mono text-sm font-bold text-purple-700 dark:text-purple-400">POST /api/external/v1/documents/bulk-convert-billportal</p>
                              <p className="text-xs text-muted-foreground mt-1">Bulk convert all completed documents that haven't been converted yet.</p>
                            </div>

                            <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-none border-l-4 border-green-500">
                              <p className="font-mono text-sm font-bold text-green-700 dark:text-green-400">GET /api/external/v1/exports</p>
                              <p className="text-xs text-muted-foreground mt-1">List all BillPortal format exports.</p>
                              <div className="mt-2 space-y-1 text-xs">
                                <p><span className="font-semibold">Query Params:</span></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><code className="bg-muted px-1">pulled</code> - Filter by pulled status (true/false)</li>
                                  <li><code className="bg-muted px-1">limit</code> - Max records (default: 100)</li>
                                </ul>
                              </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-950/30 p-3 rounded-none border-l-4 border-slate-500">
                              <p className="font-mono text-sm font-bold">POST /api/external/v1/exports/mark-pulled</p>
                              <p className="text-xs text-muted-foreground mt-1">Mark exports as pulled after syncing to your system.</p>
                              <div className="mt-2 text-xs">
                                <p><span className="font-semibold">Body:</span> <code className="bg-muted px-1">{`{"bill_track_ids": ["id1", "id2"]}`}</code></p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Reprocess APIs */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                            Reprocess Documents
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-orange-50 dark:bg-orange-950/30 p-3 rounded-none border-l-4 border-orange-500">
                              <p className="font-mono text-sm font-bold text-orange-700 dark:text-orange-400">POST /api/external/v1/documents/by-external-id/{'{external_id}'}/reprocess</p>
                              <p className="text-xs text-muted-foreground mt-1">Reprocess a document that is stuck or failed. Useful for re-extracting data.</p>
                            </div>

                            <div className="bg-orange-50 dark:bg-orange-950/30 p-3 rounded-none border-l-4 border-orange-500">
                              <p className="font-mono text-sm font-bold text-orange-700 dark:text-orange-400">POST /api/external/v1/documents/reprocess/{'{external_id}'}</p>
                              <p className="text-xs text-muted-foreground mt-1">Alternative endpoint: Reprocess by external_id (in URL path).</p>
                              <div className="mt-2 text-xs">
                                <p><span className="font-semibold">Body (optional):</span> <code className="bg-muted px-1">{`{"mode": "full"}`}</code> or <code className="bg-muted px-1">{`{"mode": "ocr_only"}`}</code></p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Webhooks */}
                        <div className="border border-border rounded-none p-4">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                            Webhooks (Optional)
                          </h3>
                          
                          <div className="space-y-4">
                            <div className="bg-teal-50 dark:bg-teal-950/30 p-3 rounded-none border-l-4 border-teal-500">
                              <p className="font-mono text-sm font-bold text-teal-700 dark:text-teal-400">PUT /api/external/v1/webhooks</p>
                              <p className="text-xs text-muted-foreground mt-1">Configure a webhook URL to receive notifications when documents are processed.</p>
                              <div className="mt-2 text-xs">
                                <p><span className="font-semibold">Body:</span> <code className="bg-muted px-1">{`{"webhook_url": "https://your-server.com/webhook", "events": ["document.completed", "document.failed"]}`}</code></p>
                              </div>
                            </div>

                            <div className="bg-slate-50 dark:bg-slate-950/30 p-3 rounded-none border-l-4 border-slate-500">
                              <p className="font-mono text-sm font-bold">GET /api/external/v1/webhooks</p>
                              <p className="text-xs text-muted-foreground mt-1">List configured webhooks for your organization.</p>
                            </div>
                          </div>
                        </div>

                        {/* Response Format */}
                        <div className="border border-border rounded-none p-4 bg-slate-50 dark:bg-slate-950/30">
                          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                            <Info className="h-5 w-5" />
                            BillPortal JSON Format Structure
                          </h3>
                          <p className="text-xs text-muted-foreground mb-3">When BillPortal Format is enabled, parsed data follows this structure:</p>
                          <div className="bg-slate-900 text-slate-100 p-3 rounded text-[10px] font-mono overflow-x-auto max-h-64">
                            <pre>{`{
  "billTrackId": "uuid",
  "customerName": "ACME CORP",
  "supplierName": "UTILITY CO",
  "invoiceNumber": "INV-12345",
  "invoiceDate": "2025-01-15",
  "dueDate": "2025-02-01",
  "accountNumber": "ACC-001",
  "totalAmountDue": 1234.56,
  "currentBillAmount": 1100.00,
  "previousBillAmount": 134.56,
  "isConsolidateBill": "YES" | "NO",
  "parseridentifiersdata": [
    {
      "identifier": "meter-account-no",
      "utilityType": "Electricity",
      "siteAddress": "123 Main St",
      "periodFrom": "2025-01-01",
      "periodTo": "2025-01-31",
      "serviceTotal": 500.00,
      "serviceUsage": 1500,
      "parserchargesdata": [
        {
          "meterNumber": "MTR-001",
          "category": "Energy",
          "lineItem": "Usage Charge",
          "quantity": "1500",
          "quantityUOM": "kWh",
          "rate": 0.10,
          "total": 150.00
        }
      ]
    }
  ]
}`}</pre>
                          </div>
                        </div>

                        {/* Tips */}
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-none p-4">
                          <h3 className="font-bold text-sm mb-2 flex items-center gap-2 text-amber-800 dark:text-amber-300">
                            <AlertTriangle className="h-4 w-4" />
                            Important Notes
                          </h3>
                          <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
                            <li>Enable "BillPortal Format Export" above for automatic format conversion</li>
                            <li>Use <code className="bg-amber-100 dark:bg-amber-900 px-1">external_id</code> to track documents with your internal IDs</li>
                            <li>Poll status endpoint or configure webhooks to know when processing completes</li>
                            <li>Rate limit: 200 uploads per hour per organization</li>
                          </ul>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-6">
                  {/* BillPortal Format Integration */}
                  <div className="border border-border rounded-none p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-lg">BillPortal Format Export</h3>
                          <Badge variant="outline" className="rounded-none text-xs">Beta</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                          Automatically convert parsed bills to BillPortal export format for integration with external systems.
                          When enabled, all newly processed bills will be transformed and stored in the standardized format.
                        </p>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="enable_billportal_format"
                            data-testid="enable-billportal-format-checkbox"
                            checked={integrationSettings.enable_billportal_format}
                            onChange={(e) => setIntegrationSettings({
                              ...integrationSettings,
                              enable_billportal_format: e.target.checked
                            })}
                            className="h-4 w-4 rounded-none border-border"
                          />
                          <Label htmlFor="enable_billportal_format" className="text-sm cursor-pointer">
                            Enable BillPortal Format
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    className="rounded-none font-bold uppercase"
                    onClick={handleSaveIntegrationSettings}
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Integration Settings'}
                  </Button>
                  
                  {/* API Key Management */}
                  <div className="border border-border rounded-none p-6 mt-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-bold text-lg">External API Access</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Generate an API key to allow external systems to pull BillPortal format data via REST API.
                    </p>
                    
                    {/* API Key Status */}
                    <div className="bg-muted/50 p-4 rounded-none mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">API Key Status</p>
                          <div className="flex items-center gap-2 mt-1">
                            {apiKeyStatus.hasKey ? (
                              apiKeyStatus.isActive ? (
                                <Badge variant="outline" className="rounded-none text-xs bg-green-50 text-green-700 border-green-200">Active</Badge>
                              ) : (
                                <Badge variant="outline" className="rounded-none text-xs bg-red-50 text-red-700 border-red-200">Revoked</Badge>
                              )
                            ) : (
                              <Badge variant="outline" className="rounded-none text-xs">Not Generated</Badge>
                            )}
                            {apiKeyStatus.createdAt && (
                              <span className="text-xs text-muted-foreground">
                                Created: {new Date(apiKeyStatus.createdAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none"
                            onClick={handleGenerateApiKey}
                            disabled={generatingKey}
                          >
                            {generatingKey ? 'Generating...' : apiKeyStatus.hasKey ? 'Regenerate Key' : 'Generate Key'}
                          </Button>
                          {apiKeyStatus.hasKey && apiKeyStatus.isActive && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-none text-red-600 hover:text-red-700"
                              onClick={handleRevokeApiKey}
                            >
                              Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Show newly generated API key */}
                    {newApiKey && (
                      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-none mb-4">
                        <p className="text-sm font-medium text-yellow-800 mb-2">
                          ⚠️ Copy your API key now! It won't be shown again.
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-white p-2 text-sm font-mono border border-yellow-300 rounded-none overflow-x-auto">
                            {newApiKey}
                          </code>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none shrink-0"
                            onClick={() => copyToClipboard(newApiKey)}
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* API Documentation */}
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p className="font-medium">API Endpoints:</p>
                      
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-foreground mb-1">📤 Upload Bills:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs font-mono">
                          <li>POST /api/external/v1/documents/upload (multipart/form-data)</li>
                          <li>GET /api/external/v1/documents/{'{documentId}'} (check status)</li>
                        </ul>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-foreground mb-1">📥 Export Data:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs font-mono">
                          <li>GET /api/external/v1/exports?pulled=false</li>
                          <li>GET /api/external/v1/exports/{'{billTrackId}'}</li>
                          <li>POST /api/external/v1/exports/mark-pulled</li>
                        </ul>
                      </div>
                      
                      <p className="text-xs mt-2">
                        Use header: <code className="bg-muted px-1">X-API-Key: your_api_key</code>
                      </p>
                      
                      <div className="mt-4 p-3 bg-muted/50 rounded-none border border-border">
                        <p className="text-xs font-semibold text-foreground mb-2">Example: Upload PDF via cURL</p>
                        <code className="text-[10px] font-mono block whitespace-pre-wrap break-all">
{`curl -X POST "${window.location.origin}/api/external/v1/documents/upload" \\
  -H "X-API-Key: your_api_key" \\
  -F "file=@/path/to/bill.pdf"`}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && isAdmin && (
              <div className="border border-border rounded-none bg-card" data-testid="users-tab-panel">
                <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <h2 className="text-2xl font-bold tracking-tight">Team Members</h2>
                    <Select
                      value={userStatusFilter}
                      onValueChange={setUserStatusFilter}
                    >
                      <SelectTrigger className="w-32 rounded-none h-8 text-xs" data-testid="users-status-filter">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="invited">Invited</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Search name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-64 rounded-none h-8 text-xs"
                      data-testid="users-search-input"
                    />
                    <span className="text-sm text-muted-foreground" data-testid="users-count">
                      {users
                        .filter(u => userStatusFilter === 'all' || u.status === userStatusFilter)
                        .filter(u => {
                          if (!userSearch) return true;
                          const q = userSearch.toLowerCase();
                          return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                        })
                        .length} user(s)
                    </span>
                  </div>
                  <Button
                    className="rounded-none"
                    onClick={() => setShowInvite(!showInvite)}
                    data-testid="invite-user-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite User
                  </Button>
                </div>

                {showInvite && (
                  <div className="p-6 border-b border-border bg-muted/30">
                    <form onSubmit={handleInviteUser} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase mb-1 block">Email</Label>
                          <Input
                            type="email"
                            className="rounded-none"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            placeholder="user@example.com"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase mb-1 block">Name</Label>
                          <Input
                            className="rounded-none"
                            value={inviteForm.name}
                            onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                            placeholder="User name"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs uppercase mb-1 block">Role</Label>
                        <Select
                          value={inviteForm.role}
                          onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                        >
                          <SelectTrigger className="rounded-none">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin - Full access</SelectItem>
                            <SelectItem value="editor">Editor - Can edit documents</SelectItem>
                            <SelectItem value="viewer">Viewer - Read only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="rounded-none">Send Invite</Button>
                        <Button type="button" variant="outline" className="rounded-none" onClick={() => setShowInvite(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead className="bg-muted/40 border-b border-border">
                      <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left px-4 py-2.5 font-semibold">User</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Role</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Last Login</th>
                        <th className="text-center px-4 py-2.5 font-semibold">
                          <div className="inline-flex items-center gap-1">
                            PF Review Authorisation
                            <span title="Email this user when a new Payment File is published for review" className="text-muted-foreground">
                              <Info className="h-3 w-3" />
                            </span>
                          </div>
                        </th>
                        <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users
                        .filter(member => userStatusFilter === 'all' || member.status === userStatusFilter)
                        .filter(member => {
                          if (!userSearch) return true;
                          const q = userSearch.toLowerCase();
                          return (member.name || '').toLowerCase().includes(q) || (member.email || '').toLowerCase().includes(q);
                        })
                        .map((member) => (
                        <tr key={member.id} className="border-b border-border/60 hover:bg-muted/20 transition-colors" data-testid={`user-row-${member.user_id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{member.name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{member.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              className={`rounded-none text-[10px] ${
                                member.status === 'inactive' ? 'bg-red-100 text-red-800' :
                                member.status === 'invited' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}
                            >
                              {member.status || 'active'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Select
                              value={member.role}
                              onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                              disabled={member.user_id === user?.id}
                            >
                              <SelectTrigger className="w-28 rounded-none h-7 text-xs" data-testid={`user-role-${member.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="editor">Editor</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {member.status === 'invited'
                              ? <>Invited {formatDateTime(member.invited_at)}</>
                              : formatDateTime(member.last_login_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Switch
                              checked={!!member.pf_reviewer}
                              onCheckedChange={(v) => handleTogglePfReviewer(member.user_id, v)}
                              disabled={member.status === 'inactive'}
                              data-testid={`pf-reviewer-switch-${member.user_id}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              {member.user_id !== user?.id && (
                                <>
                                  {member.status === 'active' ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-yellow-600"
                                      onClick={() => handleUpdateUserStatus(member.user_id, 'inactive')}
                                      title="Deactivate user"
                                      data-testid={`deactivate-user-${member.user_id}`}
                                    >
                                      <Power className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 rounded-none text-green-600"
                                      onClick={() => handleUpdateUserStatus(member.user_id, 'active')}
                                      title="Activate user"
                                      data-testid={`activate-user-${member.user_id}`}
                                    >
                                      <Power className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 rounded-none text-destructive hover:text-destructive"
                                    onClick={() => handleRemoveUser(member.user_id)}
                                    title="Remove from organization"
                                    data-testid={`remove-user-${member.user_id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No team members yet</td></tr>
                      )}
                      {users.length > 0 && users.filter(u => {
                        if (userStatusFilter !== 'all' && u.status !== userStatusFilter) return false;
                        if (!userSearch) return true;
                        const q = userSearch.toLowerCase();
                        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                      }).length === 0 && (
                        <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No users match your search</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


            {/* Feature Visibility Tab */}
            {activeTab === 'feature-visibility' && isAdmin && (
              <FeatureVisibilityTab users={users} currentAccount={currentAccount} fetchUsers={fetchUsers} />
            )}

            {/* Inbox Tab */}
            {activeTab === 'inbox' && (
              <div className="border border-border rounded-none p-8 bg-card">
                <h2 className="text-2xl font-bold tracking-tight mb-6">Email Inbox</h2>
                <p className="text-muted-foreground mb-6">
                  Receive bills via email. Forward bills to your inbox address and they&apos;ll be automatically processed.
                </p>
                
                {inboxSettings ? (
                  <div className="space-y-6">
                    {/* Inbox Email */}
                    <div className="p-4 bg-muted/30 rounded-none">
                      <Label className="text-xs uppercase tracking-wide font-medium mb-2 block">
                        Inbox Email Address
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={inboxSettings.inbox_email}
                          readOnly
                          className="rounded-none font-mono bg-background"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none shrink-0"
                          onClick={() => copyToClipboard(inboxSettings.inbox_email)}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Forward bills to this email address. PDF attachments and ZIP files will be automatically processed.
                      </p>
                    </div>

                    {/* Mailgun Webhook URL - Hidden from org users, visible only to super admin */}
                    {isSuperAdmin() && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-none">
                      <Label className="text-xs uppercase tracking-wide font-medium mb-2 block text-amber-800 dark:text-amber-200">
                        Mailgun Webhook URL (Admin Setup Required)
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value="https://app.billportal.ai/api/inbound-email/webhook"
                          readOnly
                          className="rounded-none font-mono text-xs bg-background"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none shrink-0"
                          onClick={() => copyToClipboard('https://app.billportal.ai/api/inbound-email/webhook')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="text-xs text-amber-700 dark:text-amber-300 mt-2 space-y-1">
                        <p><strong>Important:</strong> This URL must be configured in Mailgun Routes for email forwarding to work.</p>
                        <p className="font-mono text-[10px] bg-amber-100 dark:bg-amber-900/30 p-1 mt-1">
                          Route filter: match_recipient(&quot;.*@inbound.billportal.io&quot;)
                        </p>
                      </div>
                    </div>
                    )}

                    {/* Legacy "Direct API Endpoint" section removed per Feb-2026 cleanup —
                        the Mailgun Webhook URL above is the single source of truth. */}

                    {/* Queue Stats */}
                    <div className="p-4 bg-muted/30 rounded-none">
                      <Label className="text-xs uppercase tracking-wide font-medium mb-3 block">
                        Inbox Queue Status
                      </Label>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold">{inboxSettings.queue_stats?.pending || 0}</div>
                          <div className="text-xs text-muted-foreground">Pending</div>
                        </div>
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold text-yellow-600">{inboxSettings.queue_stats?.processing || 0}</div>
                          <div className="text-xs text-muted-foreground">Processing</div>
                        </div>
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold text-green-600">{inboxSettings.queue_stats?.completed || 0}</div>
                          <div className="text-xs text-muted-foreground">Completed</div>
                        </div>
                        <div className="text-center p-3 bg-background rounded-none border border-border">
                          <div className="text-2xl font-bold text-red-600">{inboxSettings.queue_stats?.failed || 0}</div>
                          <div className="text-xs text-muted-foreground">Failed</div>
                        </div>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="p-4 border border-border rounded-none bg-blue-50 dark:bg-blue-950/20">
                      <h3 className="font-medium mb-2 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        How to Use Your Inbox
                      </h3>
                      <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                        <li><strong>Forward bills</strong> to your inbox email address above</li>
                        <li>Attach <strong>PDFs directly</strong> or as a <strong>ZIP file</strong> containing multiple bills</li>
                        <li>Bills will be <strong>automatically extracted</strong> and appear in your Intake Hub</li>
                        <li>Look for the <Mail className="h-3 w-3 inline text-blue-500" /> icon to identify emailed bills</li>
                      </ol>
                      <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                        <strong>Note:</strong> Email inbox is configured by the BillPortal platform. Simply forward bills to your unique inbox address - no setup required!
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 mx-auto text-muted-foreground animate-spin" />
                    <p className="text-muted-foreground mt-2">Loading inbox settings...</p>
                  </div>
                )}
              </div>
            )}

            {/* Trusted Devices Tab */}
            {activeTab === 'trusted-devices' && (
              <TrustedDevicesTab />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const FEATURE_FLAGS = [
  { key: 'pf_validation_warnings', label: 'PF Validation Warnings', description: 'Show overdue and due-soon warning icons on Payment File bills' },
  { key: 'pf_issue_flags', label: 'PF Issue Flags', description: 'Show issue badges (NO GL, NO ADDR, MISMATCH, etc.) on Payment File detail rows' },
  { key: 'bill_payment_recording', label: 'Bill Payment Recording', description: 'Allow recording payments directly from the Bill View page' },
  { key: 'bill_sync_trigger', label: 'Bill Sync Trigger', description: 'Allow triggering bill sync from the Bills page' },
];

function FeatureVisibilityTab({ users, currentAccount, fetchUsers }) {
  const [saving, setSaving] = useState({});

  const handleToggle = async (user, featureKey, checked) => {
    const userId = user.user_id || user.id;
    setSaving(prev => ({ ...prev, [`${userId}-${featureKey}`]: true }));
    try {
      const current = user.feature_visibility || {};
      const updated = { ...current, [featureKey]: checked };
      await api.put(`/accounts/${currentAccount.id}/users/${userId}/feature-visibility`, {
        feature_visibility: updated,
      });
      toast.success(`Updated ${featureKey.replace(/_/g, ' ')} for ${user.name}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update feature visibility');
    } finally {
      setSaving(prev => ({ ...prev, [`${userId}-${featureKey}`]: false }));
    }
  };

  return (
    <div className="border border-border rounded-none bg-card" data-testid="feature-visibility-tab">
      <div className="p-6 border-b border-border">
        <h2 className="text-2xl font-bold tracking-tight">Feature Visibility</h2>
        <p className="text-sm text-muted-foreground mt-1">Control which features are visible to each user. Toggle features on/off per user.</p>
      </div>
      <div className="p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[200px]">User</th>
              {FEATURE_FLAGS.map(f => (
                <th key={f.key} className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground" title={f.description}>
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.filter(u => u.status !== 'inactive').map(user => {
              const userId = user.user_id || user.id;
              const fv = user.feature_visibility || {};
              return (
                <tr key={userId} className="border-b border-border/50 hover:bg-muted/30" data-testid={`fv-row-${userId}`}>
                  <td className="py-3 px-2">
                    <div>
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </td>
                  {FEATURE_FLAGS.map(f => (
                    <td key={f.key} className="text-center py-3 px-3">
                      <Switch
                        checked={fv[f.key] === true}
                        onCheckedChange={(checked) => handleToggle(user, f.key, checked)}
                        disabled={saving[`${userId}-${f.key}`]}
                        data-testid={`fv-${userId}-${f.key}`}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={FEATURE_FLAGS.length + 1} className="text-center py-8 text-muted-foreground">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const TrustedDevicesTab = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/trusted-devices');
      setDevices(data.devices || []);
    } catch {
      toast.error('Could not load trusted devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (id, label) => {
    if (!window.confirm(`Revoke trusted access for "${label || id}"? You'll have to enter the verification code next time you sign in from that browser.`)) return;
    setRevoking(id);
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success('Trusted device revoked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not revoke');
    } finally { setRevoking(null); }
  };

  const clearThisDevice = async () => {
    try {
      await api.post('/auth/trusted-devices/clear-cookie');
      toast.success('Trust cookie cleared on this device');
    } catch {
      toast.error('Could not clear');
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const fmtExpiry = (iso) => {
    if (!iso) return '—';
    try {
      const ms = new Date(iso).getTime() - Date.now();
      const d = Math.round(ms / (1000 * 60 * 60 * 24));
      if (d <= 0) return 'expired';
      if (d === 1) return 'tomorrow';
      return `in ${d} days`;
    } catch { return iso; }
  };

  const guessIcon = (label) => {
    const t = (label || '').toLowerCase();
    if (t.includes('iphone') || t.includes('android')) return Smartphone;
    return Monitor;
  };

  return (
    <div className="border border-border rounded-none p-8 bg-card" data-testid="trusted-devices-tab">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-2 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> Trusted Devices
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Browsers you've marked "Trust this device for 30 days" on the
            verification step. They skip the OTP prompt at sign-in.
            Revoke any device you no longer recognise — the next sign-in
            from that browser will require the full verification code.
          </p>
        </div>
        <Button
          data-testid="td-clear-this-device"
          variant="outline"
          size="sm"
          className="rounded-none shrink-0"
          onClick={clearThisDevice}
        >
          Sign me out of trust on this browser
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <Clock className="h-6 w-6 mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : devices.length === 0 ? (
        <div className="border border-dashed border-border rounded-none py-12 text-center text-muted-foreground">
          <ShieldCheck className="h-8 w-8 mx-auto opacity-30 mb-2" />
          <div className="font-medium">No trusted devices</div>
          <div className="text-xs mt-1">Check "Trust this device for 30 days" on the next sign-in to add this browser.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="td-list">
          {devices.map((d) => {
            const Icon = guessIcon(d.device_label);
            return (
              <li
                key={d.id}
                data-testid={`td-row-${d.id}`}
                className="flex items-center justify-between gap-3 border border-border rounded-none p-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="rounded-none bg-emerald-50 p-2 text-emerald-700 shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {d.device_label || 'Unknown device'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Added {fmt(d.created_at)} · last used {fmt(d.last_used_at)}
                      {d.ip ? ` · IP ${d.ip}` : ''}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Expires {fmtExpiry(d.expires_at)}
                    </div>
                  </div>
                </div>
                <Button
                  data-testid={`td-revoke-${d.id}`}
                  variant="outline"
                  size="sm"
                  className="rounded-none shrink-0 text-rose-600 hover:bg-rose-50"
                  disabled={revoking === d.id}
                  onClick={() => revoke(d.id, d.device_label)}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {revoking === d.id ? 'Revoking…' : 'Revoke'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};


export default Settings;
